import {
    Table,
    Button,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    Sheet,
    Typography,
    Chip,
    Stack,
    Divider,
    Select,
    Option,
    IconButton,
    Box,
    CircularProgress,
    Input,
    FormLabel,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    Switch,
    FormControl,
} from "@mui/joy";
import { Close, Search } from "@mui/icons-material";
import { PageStandard } from "../utils/PageStandard";
import { useEffect, useMemo, useState } from "react";
import { Experience, StickerModel, UserModel } from "../utils/Model";
import {
    adminEditUser,
    assignSticker,
    deleteUser,
    getExperiences,
    getStickers,
    getUserStickers,
    unassignSticker,
    updateUserExperiences,
    getUsers,
} from "../utils/ViewModel";
import { formatDateTime } from "../utils/Utils";


export function Users() {
    const [users, setUsers] = useState<UserModel[]>([]);
    const [experiences, setExperiences] = useState<Experience[]>([]);
    const [allStickers, setAllStickers] = useState<StickerModel[]>([]);
    const [openUser, setOpenUser] = useState<UserModel | null>(null);
    const [search, setSearch] = useState("");

    // stickers for the currently open user
    const [userStickers, setUserStickers] = useState<StickerModel[]>([]);
    const [stickersLoading, setStickersLoading] = useState(false);
    const [selectedStickerId, setSelectedStickerId] = useState<string | null>(null);

    // edit form state
    const [editData, setEditData] = useState<Partial<UserModel>>({});
    const [editSaving, setEditSaving] = useState(false);

    // experiences tab
    const [expSaving, setExpSaving] = useState(false);
    const [selectedExpId, setSelectedExpId] = useState<string | null>(null);

    useEffect(() => {
        getUsers().then(setUsers).catch(() => setUsers([]));
        getExperiences().then(setExperiences).catch(() => setExperiences([]));
        getStickers().then(setAllStickers).catch(() => setAllStickers([]));
    }, []);

    useEffect(() => {
        if (!openUser) { setUserStickers([]); return; }
        setEditData({
            firstname: openUser.firstname,
            lastname: openUser.lastname,
            email: openUser.email,
            phone: openUser.phone,
            birthdate: openUser.birthdate,
            gender: openUser.gender,
            marketing_consent: openUser.marketing_consent,
        });
        loadUserStickers(openUser.id);
    }, [openUser]);

    async function loadUserStickers(userId: string) {
        setStickersLoading(true);
        setUserStickers(await getUserStickers(userId));
        setStickersLoading(false);
    }

    async function handleAssign() {
        if (!openUser || !selectedStickerId) return;
        const ok = await assignSticker(openUser.id, selectedStickerId);
        if (ok) { setSelectedStickerId(null); await loadUserStickers(openUser.id); }
    }

    async function handleUnassign(stickerId: string) {
        if (!openUser) return;
        const ok = await unassignSticker(openUser.id, stickerId);
        if (ok) await loadUserStickers(openUser.id);
    }

    async function handleSaveEdit() {
        if (!openUser) return;
        setEditSaving(true);
        const ok = await adminEditUser(openUser.id, {
            firstname: editData.firstname,
            lastname: editData.lastname,
            email: editData.email ?? openUser.email,
            phone: editData.phone,
            birthdate: editData.birthdate,
            gender: editData.gender,
            marketing_consent: editData.marketing_consent,
        });
        if (ok) {
            const updated = await getUsers();
            setUsers(updated);
            const refreshed = updated.find((u) => u.id === openUser.id);
            if (refreshed) setOpenUser(refreshed);
        }
        setEditSaving(false);
    }

    async function handleAddExperience() {
        if (!openUser || !selectedExpId) return;
        const current = openUser.experiences_managed || [];
        if (current.includes(selectedExpId)) return;
        setExpSaving(true);
        const next = [...current, selectedExpId];
        const ok = await updateUserExperiences(openUser.id, next);
        if (ok) {
            const updated = await getUsers();
            setUsers(updated);
            const refreshed = updated.find((u) => u.id === openUser.id);
            if (refreshed) setOpenUser(refreshed);
            setSelectedExpId(null);
        }
        setExpSaving(false);
    }

    async function handleRemoveExperience(expId: string) {
        if (!openUser) return;
        setExpSaving(true);
        const next = (openUser.experiences_managed || []).filter((id) => id !== expId);
        const ok = await updateUserExperiences(openUser.id, next);
        if (ok) {
            const updated = await getUsers();
            setUsers(updated);
            const refreshed = updated.find((u) => u.id === openUser.id);
            if (refreshed) setOpenUser(refreshed);
        }
        setExpSaving(false);
    }

    const expById = useMemo(() => {
        const map = new Map<string, Experience>();
        for (const e of experiences) map.set(e.id, e);
        return map;
    }, [experiences]);

    const assignableStickers = allStickers.filter(
        (s) => !userStickers.find((us) => us.id === s.id)
    );

    const managedExperiences = (openUser?.experiences_managed || []).map((id) => ({
        id,
        title: expById.get(id)?.title || id,
        status: expById.get(id)?.status,
    }));

    const unassignedExperiences = experiences.filter(
        (e) => !(openUser?.experiences_managed || []).includes(e.id)
    );

    const filteredUsers = users.filter((u) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (u.firstname || "").toLowerCase().includes(q) ||
            (u.lastname || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q)
        );
    });

    function field(label: string, key: keyof UserModel, placeholder?: string) {
        return (
            <FormControl key={key}>
                <FormLabel>{label}</FormLabel>
                <Input
                    size="sm"
                    placeholder={placeholder}
                    value={(editData[key] as string) ?? ""}
                    onChange={(e) => setEditData((d) => ({ ...d, [key]: e.target.value }))}
                />
            </FormControl>
        );
    }

    return (
        <PageStandard title="Utenti" sidebarVisible={true}>
            {/* Search + count */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Input
                    placeholder="Cerca per nome o email…"
                    startDecorator={<Search />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, maxWidth: 400 }}
                />
                <Typography level="body-sm" color="neutral">
                    {filteredUsers.length} / {users.length} utenti
                </Typography>
            </Stack>

            <Table aria-label="Utenti" size="md" stickyHeader>
                <thead>
                    <tr>
                        <th style={{ width: "25%" }}>Nome</th>
                        <th style={{ width: "30%" }}>Email</th>
                        <th style={{ width: "15%" }}>Telefono</th>
                        <th style={{ width: "15%" }}>Iscritto il</th>
                        <th style={{ width: "15%" }}>Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredUsers.map((u) => (
                        <tr key={u.id}>
                            <td>
                                <Typography level="title-sm">{u.firstname} {u.lastname}</Typography>
                            </td>
                            <td><Typography level="body-sm">{u.email}</Typography></td>
                            <td><Typography level="body-sm">{u.phone || "—"}</Typography></td>
                            <td>
                                <Typography level="body-xs" color="neutral">
                                    {formatDateTime(u.created_at) || "—"}
                                </Typography>
                            </td>
                            <td>
                                <Button size="sm" variant="soft" onClick={() => setOpenUser(u)}>
                                    Gestisci
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal open={!!openUser} onClose={() => setOpenUser(null)}>
                <ModalDialog layout="center" size="lg" sx={{ width: "min(640px, 95vw)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <ModalClose />
                    <DialogTitle sx={{ pb: 0 }}>
                        {openUser?.firstname} {openUser?.lastname}
                        <Typography component="span" level="body-sm" color="neutral" sx={{ ml: 1 }}>
                            {openUser?.email}
                        </Typography>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 1, overflow: "auto" }}>
                        <Tabs defaultValue="dati" size="sm">
                            <TabList sx={{ mb: 2 }}>
                                <Tab value="dati">Dati</Tab>
                                <Tab value="esperienze">Esperienze</Tab>
                                <Tab value="sticker">Sticker</Tab>
                                <Tab value="altro">Altro</Tab>
                            </TabList>

                            {/* ── Dati ── */}
                            <TabPanel value="dati" sx={{ p: 0 }}>
                                <Stack spacing={1.5}>
                                    <Typography level="body-xs" color="neutral">ID: {openUser?.id}</Typography>
                                    <Divider />
                                    {field("Nome", "firstname", "Nome")}
                                    {field("Cognome", "lastname", "Cognome")}
                                    {field("Email", "email", "Email")}
                                    {field("Telefono", "phone", "+39...")}
                                    {field("Data di nascita", "birthdate", "YYYY-MM-DD")}
                                    <FormControl>
                                        <FormLabel>Genere</FormLabel>
                                        <Select
                                            size="sm"
                                            value={editData.gender ?? ""}
                                            onChange={(_, v) => setEditData((d) => ({ ...d, gender: v ?? "" }))}
                                        >
                                            <Option value="">—</Option>
                                            <Option value="M">Maschio</Option>
                                            <Option value="F">Femmina</Option>
                                            <Option value="altro">Altro</Option>
                                        </Select>
                                    </FormControl>
                                    <FormControl orientation="horizontal" sx={{ justifyContent: "space-between" }}>
                                        <FormLabel>Marketing consent</FormLabel>
                                        <Switch
                                            size="sm"
                                            checked={!!editData.marketing_consent}
                                            onChange={(e) => setEditData((d) => ({ ...d, marketing_consent: e.target.checked }))}
                                        />
                                    </FormControl>
                                    <Divider />
                                    <Typography level="body-xs" color="neutral">
                                        APN Token: {openUser?.apn_token ? openUser.apn_token.slice(0, 24) + "…" : "—"}
                                    </Typography>
                                    <Button
                                        size="sm"
                                        loading={editSaving}
                                        onClick={handleSaveEdit}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Salva modifiche
                                    </Button>
                                </Stack>
                            </TabPanel>

                            {/* ── Esperienze ── */}
                            <TabPanel value="esperienze" sx={{ p: 0 }}>
                                <Stack spacing={1.5}>
                                    <Typography level="title-sm">Esperienze gestite</Typography>
                                    {managedExperiences.length > 0 ? (
                                        <Stack spacing={0.5}>
                                            {managedExperiences.map((ex) => (
                                                <Sheet
                                                    key={ex.id}
                                                    variant="soft"
                                                    sx={{ p: 1, borderRadius: "sm", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Chip
                                                            size="sm"
                                                            color={ex.status === "public" ? "success" : ex.status === "draft" ? "warning" : "neutral"}
                                                        >
                                                            {ex.status}
                                                        </Chip>
                                                        <Typography level="body-sm">{ex.title}</Typography>
                                                    </Stack>
                                                    <IconButton
                                                        size="sm"
                                                        variant="plain"
                                                        color="danger"
                                                        onClick={() => handleRemoveExperience(ex.id)}
                                                        disabled={expSaving}
                                                    >
                                                        <Close sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Sheet>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography level="body-sm" color="neutral">Nessuna esperienza assegnata</Typography>
                                    )}

                                    <Divider />
                                    <Typography level="title-sm">Assegna esperienza</Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Select
                                            size="sm"
                                            placeholder="Seleziona esperienza…"
                                            value={selectedExpId}
                                            onChange={(_, v) => setSelectedExpId(v)}
                                            sx={{ flex: 1 }}
                                        >
                                            {unassignedExperiences.map((e) => (
                                                <Option key={e.id} value={e.id}>
                                                    {e.title} {e.status !== "public" ? `(${e.status})` : ""}
                                                </Option>
                                            ))}
                                        </Select>
                                        <Button
                                            size="sm"
                                            disabled={!selectedExpId || expSaving}
                                            loading={expSaving}
                                            onClick={handleAddExperience}
                                        >
                                            Assegna
                                        </Button>
                                    </Box>
                                </Stack>
                            </TabPanel>

                            {/* ── Sticker ── */}
                            <TabPanel value="sticker" sx={{ p: 0 }}>
                                <Stack spacing={1.5}>
                                    {stickersLoading ? (
                                        <CircularProgress size="sm" />
                                    ) : (
                                        <>
                                            {userStickers.length > 0 ? (
                                                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                                    {userStickers.map((s) => (
                                                        <Chip
                                                            key={s.id}
                                                            size="sm"
                                                            variant="soft"
                                                            color="primary"
                                                            endDecorator={
                                                                <IconButton
                                                                    size="sm"
                                                                    variant="plain"
                                                                    color="neutral"
                                                                    onClick={() => handleUnassign(s.id)}
                                                                    sx={{ borderRadius: "50%", p: 0, minWidth: 16, minHeight: 16 }}
                                                                >
                                                                    <Close sx={{ fontSize: 12 }} />
                                                                </IconButton>
                                                            }
                                                        >
                                                            {s.title}
                                                        </Chip>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography level="body-sm" color="neutral">Nessuno sticker assegnato</Typography>
                                            )}

                                            {assignableStickers.length > 0 && (
                                                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                                    <Select
                                                        size="sm"
                                                        placeholder="Aggiungi sticker…"
                                                        value={selectedStickerId}
                                                        onChange={(_, v) => setSelectedStickerId(v)}
                                                        sx={{ flex: 1 }}
                                                    >
                                                        {assignableStickers.map((s) => (
                                                            <Option key={s.id} value={s.id}>{s.title}</Option>
                                                        ))}
                                                    </Select>
                                                    <Button size="sm" disabled={!selectedStickerId} onClick={handleAssign}>
                                                        Assegna
                                                    </Button>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </TabPanel>

                            {/* ── Altro ── */}
                            <TabPanel value="altro" sx={{ p: 0 }}>
                                <Stack spacing={2}>
                                    <Typography level="body-xs" color="neutral">
                                        Iscritto il: {formatDateTime(openUser?.created_at) || "—"}
                                    </Typography>
                                    <Divider />
                                    <Typography level="title-sm" color="danger">Zona pericolosa</Typography>
                                    <Button
                                        color="danger"
                                        onClick={() => {
                                            if (window.confirm("Eliminare definitivamente l'utente? Questa azione è irreversibile.")) {
                                                deleteUser(openUser!.id)
                                                    .then((ok) => {
                                                        if (ok) {
                                                            setOpenUser(null);
                                                            getUsers().then(setUsers).catch(() => setUsers([]));
                                                        }
                                                    })
                                                    .catch((err) => alert("Errore: " + err.message));
                                            }
                                        }}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Elimina utente
                                    </Button>
                                </Stack>
                            </TabPanel>
                        </Tabs>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </PageStandard>
    );
}
